import {
  BaseTable,
  Sentiments,
  Shapes,
  Sizes,
  Tag,
  Text,
  Variants,
} from '@sede-enterprise/shell-ds-react-framework';
import {
  Database,
  InfoCircle,
} from '@sede-enterprise/shell-ds-react-framework/build/esm/components/Icon/components';
import { SiteAvailabilityControl } from '@see-power-pft-common-js/net-availability/enums/site-availability-control';
import type { ColumnDef } from '@tanstack/react-table';
import { formatInTimeZone } from 'date-fns-tz';
import {
  assetAvailabilityReasonDetails,
  siteAvailabilityReasonDetails,
} from 'pages/Availability/common/FormDrawer/constants';
import { dateFormat } from 'pages/Availability/common/ReductionsTable/constants';
import { StyledTableWrapper } from 'pages/Availability/common/ReductionsTable/ReductionsTable.styled';
import { ReductionsTableActions } from 'pages/Availability/common/ReductionsTable/ReductionsTableActions';
import { TableReduction } from 'pages/Availability/common/ReductionsTable/types';
import { PREVIEW_ENTRY_ID } from 'pages/Availability/common/utils';
import {
  BaseAssetAvailabilityEntry,
  BaseAvailabilityEntry,
  BaseSiteAvailabilityEntry,
} from 'pages/Availability/types';
import { useCallback, useMemo } from 'react';
import { Timezone } from 'types/timezone';

export const getTableData = (
  availabilityEntries: BaseAvailabilityEntry[],
  availabilityControl: SiteAvailabilityControl,
): TableReduction<BaseAvailabilityEntry>[] => {
  const reasonDetails =
    availabilityControl === SiteAvailabilityControl.AssetLevel
      ? assetAvailabilityReasonDetails
      : siteAvailabilityReasonDetails;

  const tableData = availabilityEntries.map((entry) => {
    const reduction: TableReduction<BaseAvailabilityEntry> = {
      ...entry,
      key: entry.id === PREVIEW_ENTRY_ID ? 'preview' : `reduction-${entry.id}`,
      reasonDetails:
        reasonDetails.find((r) => r.id === entry.availabilityReason)?.title ??
        null,
      sentiment: entry.id === PREVIEW_ENTRY_ID ? Sentiments.Warning : null,
    };
    return reduction;
  });
  return tableData;
};

const tableCell = (
  cell: any,
  textContent?: string,
  dataCreatedAt?: number | null,
) => {
  const textDecoration =
    cell.row.original.isCancelled === true ? 'line-through' : 'none';
  const displayText = textContent ?? `${cell.getValue() ?? '-'}`;

  const isCompleted = new Date(cell.row.original.endTime) < new Date();

  const isCompletedStyle = isCompleted ? { opacity: 0.5 } : {};

  return (
    <Text style={{ textDecoration, ...isCompletedStyle }}>
      {displayText}
      {dataCreatedAt ? (
        <span data-createdat={dataCreatedAt.toString()}></span>
      ) : null}
    </Text>
  );
};

const cellSpanPowerEnergy = ({ cell }: any) => {
  if (cell.getValue() === null) {
    return tableCell(cell, '-');
  }
  if (
    cell.column.id === 'maxImportPower' ||
    cell.column.id === 'maxExportPower'
  ) {
    return tableCell(cell, `${cell.getValue()} MW`);
  } else if (cell.column.id === 'energyCapacity') {
    return tableCell(cell, `${cell.getValue()} MWh`);
  } else {
    return tableCell(cell, `${cell.getValue()}`);
  }
};

interface ReductionsTableProps {
  availabilityEntries:
    BaseSiteAvailabilityEntry[] | BaseAssetAvailabilityEntry[];
  availabilityControl: SiteAvailabilityControl;
  previewMode?: boolean;
  timezone: Timezone;
}

export const ReductionsTable: React.FC<ReductionsTableProps> = ({
  availabilityEntries,
  availabilityControl,
  previewMode,
  timezone,
}) => {
  const isAssetLevel =
    availabilityControl === SiteAvailabilityControl.AssetLevel;

  const data = useMemo(() => {
    return getTableData(availabilityEntries ?? [], availabilityControl);
  }, [availabilityEntries, availabilityControl]);

  const getCommonColumnDefs = useCallback(<
    T extends BaseAvailabilityEntry,
  >(): Array<ColumnDef<TableReduction<T>>> => {
    return [
      {
        header: 'Reduction ID',
        accessorKey: 'id',
        cell: (cell) => {
          const content =
            cell.row.original.id === PREVIEW_ENTRY_ID
              ? 'Preview'
              : String(cell.row.original.id);
          const createdAtMs = cell.row.original.createdAt
            ? new Date(cell.row.original.createdAt).getTime()
            : null;
          return tableCell(cell, content, createdAtMs);
        },
      },
      {
        header: 'Delivery Start',
        accessorKey: 'start',
        cell: ({ cell }) => {
          return tableCell(
            cell,
            cell.row.original.start
              ? formatInTimeZone(cell.row.original.start, timezone, dateFormat)
              : '-',
          );
        },
      },
      {
        header: 'Delivery End',
        accessorKey: 'end',
        cell: ({ cell }) => {
          const endTime = cell.getValue();
          const openEndedTag = (
            <Tag
              size={Sizes.Medium}
              variant={Variants.Subtle}
              shape={Shapes.Square}
              sentiment={Sentiments.Negative}
              icon={<InfoCircle />}
              style={{ display: 'inline-flex' }}
            >
              Open ended
            </Tag>
          );

          return !endTime
            ? openEndedTag
            : tableCell(
                cell,
                cell.row.original.end
                  ? formatInTimeZone(
                      cell.row.original.end,
                      timezone,
                      dateFormat,
                    )
                  : '-',
              );
        },
      },
      {
        header: 'Reason',
        accessorKey: 'reasonDetails',
        cell: tableCell,
      },
      {
        header: 'Export Limit',
        accessorKey: 'maxExportPower',
        cell: cellSpanPowerEnergy,
      },
      {
        header: 'Import Limit',
        accessorKey: 'maxImportPower',
        cell: cellSpanPowerEnergy,
      },
      {
        header: 'Energy Limit',
        accessorKey: 'energyCapacity',
        cell: cellSpanPowerEnergy,
      },
    ];
  }, [timezone]);

  const siteColumnDefs = useMemo((): Array<
    ColumnDef<TableReduction<BaseSiteAvailabilityEntry>>
  > => {
    return [
      ...getCommonColumnDefs<BaseSiteAvailabilityEntry>(),
      {
        id: 'actions',
        header: '',
        cell: (cell) => (
          <ReductionsTableActions
            reduction={cell.row.original}
            availabilityControl={SiteAvailabilityControl.SiteLevel}
          />
        ),
      },
    ];
  }, [getCommonColumnDefs]);

  const assetColumnDefs = useMemo((): Array<
    ColumnDef<TableReduction<BaseAssetAvailabilityEntry>>
  > => {
    const commonDefs = getCommonColumnDefs<BaseAssetAvailabilityEntry>();

    // Insert the Asset column after Reduction ID (index 0)
    return [
      commonDefs[0], // Reduction ID
      {
        header: 'Asset',
        accessorKey: 'assetName',
        cell: tableCell,
      },
      ...commonDefs.slice(1), // Rest of common columns
      {
        id: 'actions',
        header: '',
        cell: (cell) => (
          <ReductionsTableActions
            reduction={cell.row.original}
            availabilityControl={SiteAvailabilityControl.AssetLevel}
          />
        ),
      },
    ];
  }, [getCommonColumnDefs]);

  const columnDefs = isAssetLevel ? assetColumnDefs : siteColumnDefs;

  return (
    <StyledTableWrapper data-testid="reductions-table">
      <BaseTable
        columns={
          columnDefs as Array<ColumnDef<TableReduction<BaseAvailabilityEntry>>>
        }
        data={data}
        sentimentKey="sentiment"
        emptyStateProps={
          previewMode
            ? {
                description:
                  'Select a start and end time to generate this preview',
              }
            : {
                title: 'There are no reductions reported for this timeframe',
                description:
                  'If your site is partially or completely unavailable, please let us know by clicking on the "Report a reduction" button in the top-right of the screen',
                className: '',
                icon: { component: <Database />, sentiment: 'neutral' },
              }
        }
      />
    </StyledTableWrapper>
  );
};
